import React, { useEffect, useState, useCallback } from 'react';
import { useAdminAuth } from '../../contexts/AdminAuthContext';
import { StatusBadge, formatDateTime } from '../../components/admin/AdminUI';
import { Check } from 'lucide-react';

export const AdminContacts = () => {
  const { authedAxios } = useAdminAuth();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState('');

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await authedAxios().get('/api/admin/contacts');
      setItems(data.contacts || []);
    } catch (e) {
      setErr(e.response?.data?.detail || 'Failed to load');
    } finally {
      setLoading(false);
    }
  }, [authedAxios]);

  useEffect(() => {
    load();
  }, [load]);

  const toggle = async (c) => {
    try {
      await authedAxios().patch(`/api/admin/contacts/${c.id}`);
      load();
    } catch (e) {
      alert(e.response?.data?.detail || 'Update failed');
    }
  };

  if (loading) return <p className="text-[#F4F1EA]/60">Loading inquiries...</p>;
  if (err) return <p className="text-red-400">{err}</p>;

  return (
    <div data-testid="admin-contacts-page" className="space-y-6">
      <div>
        <h2 className="text-3xl font-bold text-[#F4F1EA]">Contact Inquiries</h2>
        <p className="text-[#F4F1EA]/60 mt-1">{items.length} total</p>
      </div>

      <div className="space-y-4">
        {items.length === 0 && (
          <p className="text-[#F4F1EA]/60 bg-[#0B132B] border border-[#B8860B]/20 rounded-lg p-6 text-center">
            No inquiries yet.
          </p>
        )}
        {items.map((c) => (
          <div
            key={c.id}
            data-testid={`admin-contact-${c.id}`}
            className="bg-[#0B132B] border border-[#B8860B]/30 rounded-lg p-5"
          >
            <div className="flex items-start justify-between flex-wrap gap-3 mb-3">
              <div>
                <p className="font-bold text-[#F4F1EA]">{c.name}</p>
                <p className="text-xs text-[#F4F1EA]/60">
                  {c.email} {c.phone ? `• ${c.phone}` : ''}
                </p>
                <p className="text-xs text-[#F4F1EA]/50 mt-0.5">{formatDateTime(c.created_at)}</p>
              </div>
              <div className="flex items-center gap-2">
                <StatusBadge status={c.status} />
                <button
                  data-testid={`admin-contact-toggle-${c.id}`}
                  onClick={() => toggle(c)}
                  className="text-xs text-[#B8860B] hover:text-[#F4F1EA] inline-flex items-center gap-1 border border-[#B8860B]/40 rounded px-2 py-1"
                  title="Toggle resolved"
                >
                  <Check size={14} /> {c.status === 'resolved' ? 'Reopen' : 'Mark Resolved'}
                </button>
              </div>
            </div>
            <p className="font-semibold text-[#B8860B] mb-1">{c.subject}</p>
            <p className="text-[#F4F1EA]/85 whitespace-pre-wrap">{c.message}</p>
          </div>
        ))}
      </div>
    </div>
  );
};

export default AdminContacts;
